import { useCallback } from "react";
import { useAuthToken } from "@/auth/AuthProvider";

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`API ${status}`);
  }
}

type QueryScalar = string | number | boolean;

interface ApiCallOptions {
  method?: "GET" | "POST" | "DELETE" | "PUT" | "PATCH";
  body?: unknown;
  // Arrays serialize as repeated keys (``?k=a&k=b``) — that's what FastAPI's
  // ``List[str] = Query(...)`` expects. ``undefined`` / ``null`` / ``""``
  // values drop out so callers can pass ``query: { x: maybeUndef }`` without
  // guarding at the call site.
  query?: Record<string, QueryScalar | readonly QueryScalar[] | undefined | null>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: ApiCallOptions["query"]): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item === undefined || item === null || item === "") continue;
        params.append(k, String(item));
      }
      continue;
    }
    params.append(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Hook returning a typed `apiCall` bound to the current MSAL token. Token
 * acquisition runs per-call so a stale token after MSAL refresh doesn't
 * persist; the cost (one in-memory cache lookup) is trivial.
 */
export function useApi() {
  const getToken = useAuthToken();

  return useCallback(
    async <T>(path: string, opts: ApiCallOptions = {}): Promise<T> => {
      const token = await getToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (opts.body !== undefined) headers["Content-Type"] = "application/json";

      const res = await fetch(buildUrl(path, opts.query), {
        method: opts.method ?? "GET",
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        signal: opts.signal,
      });

      if (!res.ok) {
        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          // Not JSON — keep null.
        }
        throw new ApiError(res.status, body);
      }
      // 204 / empty bodies (e.g. some DELETE responses) — fall back to undefined.
      if (res.status === 204) return undefined as T;
      return (await res.json()) as T;
    },
    [getToken],
  );
}
